import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { supabase } from './supabaseClient';

import AdminPanel from './pages/admin/AdminPanel';
import WebPlayer from './pages/web/WebPlayer';
import HomeWelcomeView from './pages/web/components/HomeView';
import VideoDetailView from './pages/web/components/VideoDetailView';

import Footer from './pages/web/components/footer/Footer';
import LegalModal from './pages/web/components/footer/LegalModal';
import OfflineNotice from './pages/web/components/OfflineNotice';
import ShortsFeed from './pages/web/components/tabs/ShortsPage';
import ShortsPage from './pages/web/components/tabs/ShortsPage';

import { Home, PlaySquare, Film, Gamepad2, Compass } from 'lucide-react';

// 🛡️ CACHE DE SESSÃO: Evita requisições repetidas ao Supabase ao alternar entre abas
let cachedHomeVideos: any[] | null = null;
let cachedShortsVideos: any[] | null = null;
function HomeWrapper() {
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<any | null>(null);
  const [videos, setVideos] = useState<any[]>(cachedHomeVideos || []);

  useEffect(() => {
    async function fetchVideos() {
      // 🛡️ Se já temos os vídeos no cache, não gastamos requisições do Supabase!
      if (cachedHomeVideos) return;

      try {
        const { data, error } = await supabase
          .from('videos')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(20);
          
        if (error) throw error;
        
        if (data) {
          cachedHomeVideos = data; // Salva no cache
          setVideos(data);
        }
      } catch (err) {
        console.error("Erro ao buscar vídeos da Home:", err);
      }
    }
    fetchVideos();
  }, []);

  const handleSelectVideo = (id: string) => {
    const videoFound = videos.find(v => v.id === id);
    if (videoFound) {
      setActiveVideo(videoFound);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (activeVideo) {
    return (
      <VideoDetailView 
        video={activeVideo} 
        allVideos={videos} 
        onBack={() => setActiveVideo(null)} 
        onSelectVideo={handleSelectVideo} 
      />
    );
  }

  return (
    <HomeWelcomeView 
      onNavigateToMovies={() => navigate('/filmes')} 
      onSelectVideo={handleSelectVideo} 
    />
  );
}

// 🛡️ SHORTS WRAPPER BLINDADO CONTRA OVERFETCHING E MEMORY LEAK
function ShortsWrapper() {
  const [videos, setVideos] = useState<any[]>(cachedShortsVideos || []);

  useEffect(() => {
    async function fetchInitialShorts() {
      // 🛡️ Usa o cache se existir
      if (cachedShortsVideos) return;

      try {
        const { data, error } = await supabase
          .from('videos')
          .select('*')
          .or('post_type.eq.shorts,is_short.eq.true')
          .order('created_at', { ascending: false })
          .limit(20); 

        if (error) throw error;

        if (data) {
          cachedShortsVideos = data; // Salva no cache
          setVideos(data);
        }
      } catch (err) {
        console.error("Erro ao buscar shorts:", err);
      }
    }
    fetchInitialShorts();
  }, []);

  return <ShortsPage />;
}

function WebLayout() {
  const [activeLegalPage, setActiveLegalPage] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isShortsPage = location.pathname.includes('/shorts');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    { label: 'Início', tab: 'inicio', path: '/' },
    { label: 'Play', tab: 'playfeed', path: '/playfeed' },
    { label: 'Shorts', tab: 'shorts', path: '/shorts' },
    { label: 'Filmes', tab: 'filmes', path: '/filmes' },
    { label: 'Games', tab: 'games', path: '/games' },
  ];

  useEffect(() => {
    if (!sessionStorage.getItem('doisplay_visit_counted')) {
      sessionStorage.setItem('doisplay_visit_counted', 'true');
    }
  }, []);

  return (
   <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#000', paddingBottom: isMobile ? '85px' : '0' }}>
      <div style={{ flex: 1 }}>
        <Outlet />
      </div>

      {!isShortsPage && (
        <Footer onOpenPage={(page: string) => setActiveLegalPage(page)} />
      )}

      {activeLegalPage && (
        <LegalModal page={activeLegalPage} onClose={() => setActiveLegalPage(null)} />
      )}

      {/* 📱 BARRA DE NAVEGAÇÃO INFERIOR FIXA (Apenas Mobile) */}
      {/* 📱 BARRA DE NAVEGAÇÃO INFERIOR FIXA (Estilo App Profissional) */}
      {isMobile && (
        <nav style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          backgroundColor: '#000000',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          paddingTop: '10px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 20px))', // 👈 Margem de segurança reforçada para todos os Androids e iPhones
          zIndex: 99999,
          boxSizing: 'border-box'
        }}>
          {menuItems.map((item, index) => {
            const isActive = location.pathname === item.path;
            
            // Atribuir o ícone correspondente a cada aba
            let IconComponent = Home;
            if (item.tab === 'playfeed') IconComponent = PlaySquare;
            if (item.tab === 'shorts') IconComponent = Compass;
            if (item.tab === 'filmes') IconComponent = Film;
            if (item.tab === 'games') IconComponent = Gamepad2;

            return (
              <div 
                key={index}
                onClick={() => navigate(item.path)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: isActive ? '#E50914' : '#888',
                  fontSize: '10px',
                  fontWeight: isActive ? '700' : '400',
                  flex: 1,
                  gap: '4px'
                }}
              >
                <IconComponent size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                <span>{item.label}</span>
              </div>
            );
          })}
        </nav>
      )}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <OfflineNotice /> 
      
      <Routes>
        <Route element={<WebLayout />}>
          <Route path="/" element={<HomeWrapper />} /> {/* 👈 Retornamos o HomeWrapper para a página inicial voltar a ter o card e o visual correto */}
          <Route path="/filmes" element={<WebPlayer />} />
          <Route path="/games" element={<WebPlayer />} />
          <Route path="/shorts" element={<ShortsWrapper />} />
          <Route path="/playfeed" element={<WebPlayer />} />
        </Route>

        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
    </BrowserRouter>
  );
}